import { Form, Button, Modal, Radio } from "antd"
import { Field, change, destroy, getFormValues, propTypes, reduxForm } from "redux-form"
import { FormattedMessage, injectIntl } from "react-intl"
import React, { Component } from "react"
import { has, isFunction, pick } from "lodash"
import PropTypes from "prop-types"
import { Scrollbars } from "react-custom-scrollbars-4"
import { connect } from "react-redux"

import { ACTIONS } from "../../globals"
import { InputField, RadioGroupField } from "src/inputs/connected-input-fields"
import { bindActions, bindSelectors } from "src/store/utils"
import actions from "src/store/actions"
import { mustBeDecimal, required } from "../validators"
import selectors from "src/store/selectors"
import StepNavigators from "src/components/StepNavigators"
import { TEMP_STEP_PREFIX } from "../../components/opf-items/ProcedureSplitView/constants"

const procedureItemFields = [
  "accessRoles",
  "type",
  "icon",
  "title",
  "text",
  "mediaLibrary",
  "draft",
  "viewableByExternals",
  "nextReviewDate",
  "nextReviewDateIsApplicable",
  "contactEmail",
  "reviewDate",
  "classification",
  "editorialVersion",
  "documentOwner",
  "informationContact",
  "globalType",
  "documentNumber",
]
const itemFields = ["changeLog", "howTo", "text", "image"]

export class NotificationForm extends Component {
  state = {
    loading: false,
    publishing: false,
    previewVisible: false,
    previewImage: "",
  }

  handlePublish = () => {
    this.setState({ publishing: true })
    const { item, formValues, update, procedureFormValues } = this.props
    const isMajorUpdate = formValues.updateType === "major" ? true : false
    const mediaFiles = has(procedureFormValues, "procedureMediaFiles.fileList")
      ? procedureFormValues.procedureMediaFiles.fileList
      : []
    const mediaLinks = mediaFiles.map((file) => ({ title: file.name, url: file.url }))

    const formData = {
      ...pick(procedureFormValues, procedureItemFields),
      notification: isMajorUpdate
        ? {
            ...pick(formValues, itemFields),
            image: has(formValues, "image.file") ? formValues.image.file.url : null,
          }
        : null,
      mediaLibrary: [...mediaLinks],
      draftVersion: null,
      steps: (procedureFormValues?.steps || [])
        .filter((item) => !item.archiveStep)
        .map((item) => ({
          ...item,
          stepId: item.stepId
            ? item.stepId.startsWith(TEMP_STEP_PREFIX)
              ? null
              : item.stepId
            : null,
        })),
    }

    // take item method from modal or update
    const itemAction = this.props ? this.props.method || update : update

    // editorialVersion comes from a different form now, so in order to populate the data for BE we need to assign this value to the resulting form data
    formData.editorialVersion = formValues.editorialVersion || "1.0"

    return itemAction({ opfItem: { ...item, ...formData }, majorUpdate: isMajorUpdate })
      .then((response) => {
        if (isFunction(this.props.callback)) {
          return window.requestAnimationFrame(() => {
            !this.unmounted && this.setState({ publishing: false })
            return this.leaveEditMode({ success: true, response, majorUpdate: isMajorUpdate })
          })
        }
        window.requestAnimationFrame(() => {
          !this.unmounted && this.setState({ publishing: false })
          this.leaveEditMode({ success: true, response, majorUpdate: isMajorUpdate })
        })
      })
      .catch((error) => {
        isFunction(this.props.callback) && this.props.callback({ error })
        this.setState({ publishing: false })
      })
  }

  leaveEditMode = ({ success, response, majorUpdate }) => {
    const { closeModal, procedureEditMode, setProcedureEditMode, resizeMenu, destroyForm, form } =
      this.props
    return new Promise((resolve) => {
      this.closed = true
      window.requestAnimationFrame(() => {
        closeModal()
        if (isFunction(this.props.callback)) {
          resolve({ closed: true, success, response, majorUpdate })
          this.props.callback({ closed: true, success, response, majorUpdate })
          return
        }
        if (procedureEditMode) setProcedureEditMode(false)
        destroyForm(form)
        resizeMenu(275)
        destroyForm("procedureForm")
        resolve({ closed: true })
      })
    })
  }

  customUploadRequest = ({ onSuccess, file }) => {
    const { uploadFile, getItemBySlug } = this.props
    const item = this.props.item.opfId ? this.props.item : getItemBySlug(this.props.item.parentSlug)
    return uploadFile({ opfItem: item, file }).then((result) => onSuccess(result, result.media))
  }

  handlePreview = (file) => this.setState({ previewImage: file.thumbUrl, previewVisible: true })

  handleCancelPreview = () => this.setState({ previewVisible: false })

  removeFile = (currentFile) => {
    const { change, formValues } = this.props
    const fileList = formValues.image.fileList.filter((file) => file.uid !== currentFile.uid)
    change("image.fileList", fileList)
  }

  componentDidMount() {
    const { changeForm, procedureEditMode, item } = this.props
    const notEditingExistingItem = procedureEditMode !== ACTIONS.EDIT
    // editorial version comes from item, but its value is changed in another form, that's why we need to assign its value when we open Publish view
    changeForm("notification", "editorialVersion", item.editorialVersion || "1.0")

    if (notEditingExistingItem) changeForm("notification", "updateType", "major")
  }

  componentWillUnmount() {
    this.unmounted = true
    if (this.request && this.request.cancel) this.request.cancel("unmounted")
    isFunction(this.props.callback) && !this.closed && this.props.callback({ closed: true })
  }

  render() {
    const { publishing } = this.state
    const { intl, procedureEditMode, modalOptions } = this.props
    const { item, submitting, valid, formValues } = this.props
    const publishLabel = intl.formatMessage({ id: "button.publish" })
    const disabled = ![ACTIONS.EDIT, ACTIONS.ADD].includes(procedureEditMode) && !item.new
    return (
      <>
        <Form className="ant-form notification-form">
          <Scrollbars autoHide hideTracksWhenNotNeeded>
            <div className="ant-form__spacing">
              <Field
                disabled={disabled /* || countryOwnerEditingEnglishVariant*/}
                name="updateType"
                component={RadioGroupField}
                validate={[required]}
                button
                label={<FormattedMessage id="form.notification.selectType" />}
                className="radio-group"
              >
                <Radio value="minor">
                  <FormattedMessage id="form.notification.minorChange" />
                  {/* <span> - Do not notify users</span> */}
                  <p>
                    <FormattedMessage id="form.notification.minorChangeDescription" />
                  </p>
                </Radio>
                <Radio value="major">
                  <FormattedMessage id="form.notification.majorChange" />
                  {/* <span> - Notify users</span> */}
                  <p>
                    <FormattedMessage id="form.notification.majorChangeDrescription" />
                  </p>
                </Radio>
              </Field>
              <Field
                name="editorialVersion"
                component={InputField}
                validate={[mustBeDecimal]}
                label={`${intl.formatMessage({ id: "common.editorialVersion" })}`}
                disabled={modalOptions?.disabledEditorialVersion}
              />
              {/* TODO: replace hardcoded locale strings  */}
              {formValues && formValues.updateType === "major" ? (
                <div className="notification-form__details fade-in">
                  <Field
                    name="changeLog"
                    component={InputField}
                    label="What is changed?"
                    placeholder="In this update we changed..."
                    validate={[]}
                  />
                  <Field
                    name="howTo"
                    component={InputField}
                    label="Implementation"
                    placeholder="How should the changes be implemented..."
                    validate={[]}
                  />
                </div>
              ) : null}
            </div>
          </Scrollbars>
        </Form>
        <StepNavigators className="row">
          <div className="col-xs end-xs">
            <Button
              htmlType="button"
              type="primary"
              onClick={this.handlePublish}
              loading={publishing}
              disabled={submitting || !valid}
            >
              {formValues && formValues.updateType === "major" ? publishLabel : publishLabel}
            </Button>
          </div>
        </StepNavigators>

        <Modal
          className="media-wall-preview-modal"
          open={this.state.previewVisible}
          footer={null}
          onCancel={this.handleCancelPreview}
          centered
          style={{ position: "relative", maxHeight: "96vh", textAlign: "center" }}
        >
          <img alt="example" src={this.state.previewImage} />
        </Modal>
      </>
    )
  }
}

NotificationForm.propTypes = {
  mode: PropTypes.string,
  formValues: PropTypes.object,
  procedureValues: PropTypes.object,
  update: PropTypes.func,
  closeModal: PropTypes.func,
  destroyForm: PropTypes.func,
  getItemBySlug: PropTypes.func,
  uploadFile: PropTypes.func,
  resizeMenu: PropTypes.func,
  procedureEditMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ...propTypes,
}

const form = "notification"
const ConnectedNotificationForm = reduxForm({ form })(NotificationForm)
const mapStateToProps = bindSelectors({
  formValues: getFormValues(form),
  procedureFormValues: getFormValues("procedureForm"),
  procedureEditMode: selectors.itemSelectors.getProcedureEditModeState,
  mapToMediaLinks: selectors.mapToMediaLinks,
  getItemBySlug: selectors.itemSelectors.getItemBySlug,
  currentRole: selectors.userSelectors.getCurrentRole,
  modalOptions: selectors.uiSelectors.getModalOptions,
})
const mapDispatchToProps = bindActions({
  changeForm: change,
  update: actions.itemActions.updateItem,
  closeModal: actions.uiActions.closeFormModal,
  setProcedureEditMode: actions.itemActions.setProcedureEditMode,
  resizeMenu: actions.uiActions.resizeMenu,
  uploadFile: actions.itemActions.uploadFile,
  destroyForm: destroy,
})
const IntlNotificationForm = injectIntl(ConnectedNotificationForm)
export default connect(mapStateToProps, mapDispatchToProps)(IntlNotificationForm)
